import React, { createContext, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { setAuthHeader } from "../api/api";
import { authService } from "../api/authService";
import { LoginDto } from "../types/Auth";

type DecodedToken = {
  sub?: string;
  username?: string;
  name?: string;
  email?: string;
  exp?: number;
  iat?: number;
  [key: string]: any;
};

type AuthContextType = {
  accessToken: string | null;
  user: any | null;
  login: (data: LoginDto) => Promise<boolean>;
  logout: () => Promise<boolean>;
  setAuthToken: (token: string) => void;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<any | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    setIsAuthLoading(false);
  }, []);

  const login = async (data: LoginDto): Promise<boolean> => {
    try {
      const result = await authService.login(data);

      if (result?.data) {
        await setAccessToken(result.data.accessToken.accessToken);
        await setUser(result.data.username);
        await setAuthHeader(result.data.accessToken.accessToken);

        await sessionStorage.setItem(
          "Permissions",
          JSON.stringify(result.data.permissions)
        );

        return true; // ✅ Login successful
      } else {
        return false;
      }
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  // 🔓 Logout logic
  const logout = async (): Promise<boolean> => {
    try {
      await authService.logout();
      return true;
    } catch (error) {
      console.error("Logout error:", error);
      return false;
    } finally {
      setAccessToken(null);
      setUser(null);
      setAuthHeader(null);
    }
  };

  const setAuthToken = (token: string) => {
    try {
      if (!token) {
        throw new Error("Empty token provided to setAuthToken");
      }

      const decoded = jwtDecode<DecodedToken>(token);

      const username =
        decoded.username ||
        decoded.sub ||
        decoded.name ||
        decoded.email ||
        "UnknownUser";

      setAccessToken(token);
      setUser(username);
      setAuthHeader(token);

      console.log("Token successfully set from parent:", {
        username,
        exp: decoded.exp ? new Date(decoded.exp * 1000).toISOString() : "N/A",
      });
    } catch (err) {
      console.error("Failed to set auth token:", err);
      setAccessToken(null);
      setUser(null);
      setAuthHeader(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        user,
        login,
        logout,
        setAuthToken,
        isAuthenticated: !!accessToken,
        isAuthLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
